import {
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text
} from "@chakra-ui/react";

import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  IDepartment,
  TeamMember
} from "@/jenesys-types/jenesys-entities/config";

import { FormInput } from "@/components/forms/FormInput/FormInput";
import { customSelectStyles } from "@/util/helpers";

const defaultMemberDetails = {
  user: { first_name: "", last_name: "", email: "" },
  position: "",
  role: null,
  department: null as IDepartment | null
};

interface MemberDetailsProps {
  selectedMember: TeamMember | null;
  roles: {
    name: string;
    id: string;
  }[];
  departments: IDepartment[];
  isSavingMember: any;
  isAddingMember: any;
  isAddingDepartment: any;
  viewMemberDisclosure: any;
  onCloseModal: () => void;
  handleSubmitAddDepartment: any;
  handleSubmitAddMember: any;
  handleSubmitMemberUpdates: any;
}

export const MemberDetails: React.FC<MemberDetailsProps> = ({
  selectedMember,
  roles,
  departments,
  isSavingMember,
  isAddingMember,
  isAddingDepartment,
  viewMemberDisclosure,
  onCloseModal,
  handleSubmitAddDepartment,
  handleSubmitAddMember,
  handleSubmitMemberUpdates
}) => {
  const initialRole = roles.find((role) => role.id === selectedMember?.role);

  const { control, reset, setValue, handleSubmit } = useForm({
    defaultValues: defaultMemberDetails,
    values: {
      user: {
        first_name: selectedMember?.user.first_name || "",
        last_name: selectedMember?.user.last_name || "",
        email: selectedMember?.user.email || ""
      },
      position: selectedMember?.position || "",
      role: initialRole || null,
      department: selectedMember?.department || null
    }
  });

  const isEditing = !!selectedMember?.id;

  const handleCreateDepartment = async (inputValue: string) => {
    const data = await handleSubmitAddDepartment({ name: inputValue });

    if (data) setValue("department", data);
  };

  const handleSubmitType = (values: any, type: "close" | "reset") => {
    const payload = {
      id: selectedMember?.id, // Will be undefined if adding a new member
      first_name: values.user.first_name,
      last_name: values.user.last_name,
      email: values.user.email,
      position: values.position,
      department_id: values.department?.id,
      role: values.role.id
    };

    if (isEditing) {
      handleSubmitMemberUpdates(payload);
    } else {
      handleSubmitAddMember(payload);
    }
    if (type === "close") onCloseModal();
    if (type === "reset") reset(defaultMemberDetails);
  };

  return (
    <Modal
      size="xl"
      isCentered
      closeOnOverlayClick={false}
      motionPreset="slideInBottom"
      {...viewMemberDisclosure}
    >
      <ModalOverlay />
      <ModalContent pt={3} pb={3} px={4}>
        <ModalHeader>
          <Heading fontWeight={600} fontSize="xl">
            {isEditing
              ? "Edit member details"
              : "Invite member to your organisation"}
          </Heading>
        </ModalHeader>
        <ModalCloseButton top={6} right={6} />
        <ModalBody>
          <Stack spacing={4}>
            <Stack w="full" spacing={1}>
              <Text fontSize={13}>First name</Text>

              <FormInput
                size="sm"
                required
                h={9}
                name="user.first_name"
                control={control}
              />
            </Stack>

            <Stack w="full" spacing={1}>
              <Text fontSize={13}>Last name</Text>
              <FormInput
                size="sm"
                required
                isRequired
                h={9}
                name="user.last_name"
                control={control}
              />
            </Stack>

            <Stack w="full" spacing={1}>
              <Text fontSize={13}>Email</Text>

              <FormInput
                size="sm"
                required
                h={9}
                type="email"
                name="user.email"
                isDisabled={isEditing}
                control={control}
              />
            </Stack>

            <Stack w="full">
              <Text fontSize={13}>Department</Text>

              <Controller
                name="department"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <CreatableSelect
                    {...field}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    isDisabled={isAddingDepartment}
                    isLoading={isAddingDepartment}
                    styles={customSelectStyles("sm")}
                    onCreateOption={handleCreateDepartment}
                    formatCreateLabel={(inputValue) =>
                      `Create "${inputValue}" department`
                    }
                    getNewOptionData={(value, label) => ({
                      id: value,
                      name: label,
                      __isNew__: true
                    })}
                    className={fieldState.error && "invalid"}
                    placeholder="Select or start typing to create a new department..."
                    options={departments}
                  />
                )}
              />
            </Stack>

            <Stack w="full" spacing={1}>
              <Text fontSize={13}>Position</Text>
              <FormInput size="sm" h={9} name="position" control={control} />
            </Stack>

            <Stack w="full">
              <Text fontSize={13}>Role</Text>

              <Controller
                name="role"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <ReactSelect
                    {...field}
                    className={fieldState.error && "invalid"}
                    styles={customSelectStyles("sm")}
                    placeholder="Select Role..."
                    options={roles}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                  />
                )}
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter mt={6} mb={3} px={4} w="full">
          <HStack w="full" justify="space-between">
            <Button size="sm" h={9} variant="outline" onClick={onCloseModal}>
              Close
            </Button>
            <HStack spacing={4}>
              {!isEditing && (
                <Button
                  onClick={handleSubmit((values) =>
                    handleSubmitType(values, "reset")
                  )}
                  isDisabled={isAddingMember}
                  loadingText="Inviting..."
                  type="submit"
                  size="sm"
                  h={9}
                >
                  Send and Invite Another
                </Button>
              )}
              <Button
                onClick={handleSubmit((values) =>
                  handleSubmitType(values, "close")
                )}
                minW={115}
                size="sm"
                h={9}
                isLoading={isAddingMember || isSavingMember}
                loadingText={isEditing ? "Saving..." : "Inviting..."}
                type="submit"
              >
                {isEditing ? "Save" : "Send Invite"}
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
